import SEO from "@/components/SEO";
import { Reveal, LineReveal } from "@/components/Reveal";
import { useSettings } from "@/hooks/SettingsContext";
import { trackGiveClick } from "@/lib/analytics";
import { ArrowRight, Repeat } from "lucide-react";

export default function Give() {
  const { settings } = useSettings();
  const giveUrl = settings?.giving_url;
  const recurringUrl = settings?.recurring_giving_url || giveUrl;

  return (
    <>
      <SEO
        title="Give - Generosity Makes Ministry Possible"
        description="Give securely to support the mission of Victory Chapel, a new church in Clay County, Florida. One-time and recurring giving available."
        path="/give"
      />
      <section className="grain relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">Give</p>
          <LineReveal className="mt-4 font-display text-5xl font-900 leading-[1.02] tracking-tighter sm:text-7xl" lines={["GENEROSITY MAKES", "MINISTRY POSSIBLE."]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              "Each of you should give what you have decided in your heart to give, not reluctantly
              or under compulsion, for God loves a cheerful giver." - 2 Corinthians 9:7
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 sm:py-28" data-testid="give-section">
        <div className="mx-auto grid max-w-5xl gap-10 px-5 sm:px-8 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <h2 className="font-display text-3xl font-800 tracking-tight text-navy sm:text-4xl text-balance">
              PLANTING A CHURCH TAKES A FAMILY.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
              Every gift to Victory Chapel helps create a place where the people of Clay County can
              encounter Jesus - from kids' spaces and Sunday services to outreach in our neighborhoods.
              As a brand-new church plant, every seed matters.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              Giving is never a requirement at Victory Chapel - there's no pressure here, especially if
              you're visiting. But everyone is encouraged to give to a God who gave us everything.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="rounded-3xl bg-navy p-8 text-white shadow-lift sm:p-10">
              <h3 className="font-display text-2xl font-800 tracking-tight">GIVE SECURELY</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                Online giving is processed through our secure third-party giving partner.
                Victory Chapel never sees or stores your card information.
              </p>
              <div className="mt-7 space-y-3.5">
                {giveUrl ? (
                  <a href={giveUrl} target="_blank" rel="noopener noreferrer" onClick={trackGiveClick} data-testid="give-online-button"
                     className="flex items-center justify-center gap-2 rounded-full bg-victory px-7 py-4 font-display text-base font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
                    GIVE ONLINE <ArrowRight size={17} aria-hidden="true" />
                  </a>
                ) : (
                  <p className="rounded-2xl border border-white/15 bg-white/5 px-6 py-5 text-center text-sm text-white/70" data-testid="give-coming-soon">
                    Online giving is being set up. Check back soon - or reach out and we'll help you give another way.
                  </p>
                )}
                {recurringUrl && (
                  <a href={recurringUrl} target="_blank" rel="noopener noreferrer" onClick={trackGiveClick} data-testid="give-recurring-button"
                     className="flex items-center justify-center gap-2 rounded-full border border-white/30 px-7 py-4 font-display text-base font-700 text-white transition-colors duration-300 hover:bg-white/10">
                    SET UP RECURRING GIVING <Repeat size={17} aria-hidden="true" />
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
